import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createMix, audioContext, type Mix } from './audio';
import { paint, type Scene } from './compositor';
import { CANVAS_FPS, CANVAS_HEIGHT, CANVAS_WIDTH } from './layouts';
import { publish, WhipError, type Publication } from './whip';

/**
 * Everything the studio owns that React does not: devices, a render loop and a
 * peer connection.
 *
 * ## The canvas is a ref, not state
 *
 * It is painted thirty times a second. Anything that made React re-render at
 * that rate would spend the frame budget reconciling instead of drawing, and
 * the encoder would get a stuttering picture. So the scene arrives through a
 * ref that the loop reads, and React re-renders only when something a human
 * changed changes.
 *
 * ## Two canvases, one drawing
 *
 * The captured canvas is 720p regardless of the window; the preview is
 * whatever fits on screen. Both are painted from the same scene by the same
 * function on the same frame, which is what makes the preview trustworthy -
 * a preview drawn by different code is a preview that can be wrong exactly
 * when it matters.
 */

export type StudioStatus = 'idle' | 'starting' | 'live' | 'error';

export interface Devices {
  cameras: MediaDeviceInfo[];
  microphones: MediaDeviceInfo[];
}

export function useStudio(scene: Scene, active: boolean) {
  /*
   * The scene, mirrored into a ref for the loop.
   *
   * The loop is started once and lives for the session; it cannot close over
   * `scene` or it would paint whatever the scene was on the render that started
   * it, forever. Writing on every render is cheap and keeps the loop reading
   * the current one.
   */
  const sceneRef = useRef(scene);
  sceneRef.current = scene;

  const outputRef = useRef<HTMLCanvasElement | null>(null);
  const previewRef = useRef<HTMLCanvasElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const cameraRef = useRef<MediaStream | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const mixRef = useRef<Mix | null>(null);
  const publicationRef = useRef<Publication | null>(null);

  const [status, setStatus] = useState<StudioStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] = useState<RTCPeerConnectionState>('new');
  const [devices, setDevices] = useState<Devices>({ cameras: [], microphones: [] });
  const [cameraOn, setCameraOn] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [level, setLevel] = useState(0);

  /* ---------------------------------------------------------------------- */
  /* The render loop                                                         */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    // Nothing to paint and nothing to paint onto. Every host has this hook
    // mounted whether or not they opened the studio, and a `requestAnimationFrame`
    // loop that wakes sixty times a second to find two null refs is a battery
    // cost paid by people who are not using the feature.
    if (!active) return;

    let frame = 0;

    const draw = () => {
      frame = requestAnimationFrame(draw);

      const current = sceneRef.current;
      const withCamera: Scene = { ...current, camera: videoRef.current };

      const output = outputRef.current;

      if (output) {
        const ctx = output.getContext('2d');

        if (ctx) paint(ctx, withCamera, output.width, output.height);
      }

      const preview = previewRef.current;

      if (preview) {
        const ctx = preview.getContext('2d');

        if (ctx) paint(ctx, withCamera, preview.width, preview.height);
      }
    };

    frame = requestAnimationFrame(draw);

    return () => cancelAnimationFrame(frame);
  }, [active]);

  /* ---------------------------------------------------------------------- */
  /* Level meter                                                             */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    if (status !== 'live') return;

    // Ten times a second, not per frame: this one *does* set React state, and
    // a meter is legible at 10Hz. Thirty renders a second to move a bar four
    // pixels is the trade the render loop above exists to avoid.
    const timer = setInterval(() => setLevel(mixRef.current?.peak() ?? 0), 100);

    return () => clearInterval(timer);
  }, [status]);

  /* ---------------------------------------------------------------------- */
  /* Devices                                                                 */
  /* ---------------------------------------------------------------------- */

  const refreshDevices = useCallback(async () => {
    const all = await navigator.mediaDevices.enumerateDevices();

    setDevices({
      cameras: all.filter((d) => d.kind === 'videoinput'),
      microphones: all.filter((d) => d.kind === 'audioinput'),
    });
  }, []);

  useEffect(() => {
    void refreshDevices();

    navigator.mediaDevices.addEventListener('devicechange', refreshDevices);

    return () => navigator.mediaDevices.removeEventListener('devicechange', refreshDevices);
  }, [refreshDevices]);

  const startCamera = useCallback(async (deviceId?: string) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        deviceId: deviceId ? { exact: deviceId } : undefined,
        width: { ideal: CANVAS_WIDTH },
        height: { ideal: CANVAS_HEIGHT },
      },
      audio: false,
    });

    cameraRef.current?.getTracks().forEach((t) => t.stop());
    cameraRef.current = stream;

    const video = videoRef.current ?? document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    await video.play();
    videoRef.current = video;

    setCameraOn(true);

    // Labels are blank until permission has been granted once, so the list is
    // worth re-reading the moment it has been.
    await refreshDevices();
  }, [refreshDevices]);

  const stopCamera = useCallback(() => {
    cameraRef.current?.getTracks().forEach((t) => t.stop());
    cameraRef.current = null;

    if (videoRef.current) videoRef.current.srcObject = null;
    videoRef.current = null;

    setCameraOn(false);
  }, []);

  const startMic = useCallback(async (deviceId?: string) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: deviceId ? { exact: deviceId } : undefined,
        // On, all three. A host in a room with music playing out loud is the
        // normal case, not the exception, and raw microphone input in that
        // room is unlistenable.
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    });

    micRef.current?.getTracks().forEach((t) => t.stop());
    micRef.current = stream;

    setMicOn(true);
    await refreshDevices();
  }, [refreshDevices]);

  const stopMic = useCallback(() => {
    micRef.current?.getTracks().forEach((t) => t.stop());
    micRef.current = null;
    setMicOn(false);
  }, []);

  /* ---------------------------------------------------------------------- */
  /* Publishing                                                              */
  /* ---------------------------------------------------------------------- */

  const start = useCallback(async (whipUrl: string) => {
    setStatus('starting');
    setError(null);

    try {
      // Browsers start an AudioContext suspended until a gesture. This runs
      // from a click, which is the gesture - but only if it is awaited before
      // anything reads from the graph.
      await audioContext().resume();

      const output = outputRef.current;

      if (!output) throw new Error('The studio canvas is not ready.');

      const mix = createMix(micRef.current);
      mixRef.current = mix;

      const stream = new MediaStream([
        ...output.captureStream(CANVAS_FPS).getVideoTracks(),
        ...mix.stream.getAudioTracks(),
      ]);

      const publication = await publish(whipUrl, stream);
      publicationRef.current = publication;

      publication.onStateChange((state) => {
        setConnection(state);

        // `failed` is terminal - ICE has given up - and the host needs to know
        // rather than watch a dead preview. `disconnected` is not: it is a
        // network blip that WebRTC recovers from on its own, and tearing the
        // session down on it would turn every hiccup into a dropped broadcast.
        if (state === 'failed') {
          setStatus('error');
          setError('The connection to the stream server dropped.');
        }
      });

      setStatus('live');
    } catch (cause) {
      mixRef.current?.stop();
      mixRef.current = null;

      setStatus('error');
      setError(
        cause instanceof WhipError || cause instanceof Error
          ? cause.message
          : 'The studio could not start.',
      );
    }
  }, []);

  const stop = useCallback(async () => {
    await publicationRef.current?.stop();
    publicationRef.current = null;

    mixRef.current?.stop();
    mixRef.current = null;

    setStatus('idle');
    setConnection('new');
    setLevel(0);
  }, []);

  /* ---------------------------------------------------------------------- */
  /* Teardown                                                                */
  /* ---------------------------------------------------------------------- */

  useEffect(() => () => {
    /*
     * Devices are released on unmount, always.
     *
     * A camera light left on after the host navigated away is the single most
     * alarming thing a web app can do, and it is what happens if this is left
     * to garbage collection - `MediaStreamTrack` is not collected while the
     * device is open.
     */
    publicationRef.current?.stop();
    mixRef.current?.stop();
    cameraRef.current?.getTracks().forEach((t) => t.stop());
    micRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  return useMemo(
    () => ({
      outputRef,
      previewRef,
      status,
      error,
      connection,
      devices,
      cameraOn,
      micOn,
      level,
      startCamera,
      stopCamera,
      startMic,
      stopMic,
      start,
      stop,
      setDeckLevel: (value: number) => mixRef.current?.setDeckLevel(value),
      setMicLevel: (value: number) => mixRef.current?.setMicLevel(value),
    }),
    [
      status,
      error,
      connection,
      devices,
      cameraOn,
      micOn,
      level,
      startCamera,
      stopCamera,
      startMic,
      stopMic,
      start,
      stop,
    ],
  );
}
